import { useNavigate } from 'react-router-dom';
import { FileText, HelpCircle } from '../../icons';
import { EmptyState, ListRow, PageHeader } from '../../components/shared';
import { useAuthStore } from '../../store/authStore';
import { tLayout } from '../../lib/layout-i18n';
import { BULK_IMPORT_HELP_PATH } from './AdminBulkImportHelp';

/**
 * 도움말 홈 — `/help` (2026-09-13 · D).
 *
 * ## 왜
 * 레일 하단에 「도움말」을 세웠는데 `/help` 루트는 `<Outlet/>` 뿐이라 **빈 화면**이었다.
 * 자식 매뉴얼은 관리자용 «대량 등록» 하나뿐이다.
 *
 * ## 규율
 * ★ 정본 부품만 쓴다 — `PageHeader`(A3) · `ListRow`(E2) · `EmptyState`(G1). 새 모양을 짜지 않는다.
 * ★ 권한 밖 매뉴얼은 **목록에 안 그린다** — 눌렀는데 「권한 없음」이 뜨면 안 된다.
 * ★ 볼 것이 없으면 EmptyState 로 «다음에 할 일»(화면 제목 옆 도움말 단추)을 말한다(정본 P: 빈 화면 안내는 남긴다).
 * 매뉴얼이 늘면 `TOPICS` 에 한 줄씩 더한다.
 */

interface HelpTopic {
  to: string;
  title: string;
  subtitle: string;
  /** 볼 수 있는 역할. 없으면 모두. */
  roles?: ReadonlyArray<string>;
}

const ADMIN_ROLES = ['ADMIN', 'SYSTEM_ADMIN'] as const;

function topics(): HelpTopic[] {
  return [
    {
      to: BULK_IMPORT_HELP_PATH,
      title: tLayout('layout.help.bulk_import_title'),
      subtitle: tLayout('layout.help.bulk_import_sub'),
      roles: ADMIN_ROLES,
    },
  ];
}

export function HelpHome() {
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.user?.role ?? '');
  const visible = topics().filter((t) => !t.roles || t.roles.includes(role));

  return (
    <>
      <PageHeader title={tLayout('layout.nav.help')} description={tLayout('layout.help.description')} />
      {visible.length === 0 ? (
        <EmptyState
          icon={HelpCircle}
          title={tLayout('layout.help.empty_title')}
          description={tLayout('layout.help.empty_desc')}
        />
      ) : (
        <div role="list" aria-label={tLayout('layout.help.list_aria')}>
          {visible.map((t) => (
            <div role="listitem" key={t.to}>
              <ListRow
                leading={<FileText size={18} aria-hidden />}
                title={t.title}
                subtitle={t.subtitle}
                onClick={() => navigate(t.to)}
              />
            </div>
          ))}
        </div>
      )}
    </>
  );
}

export default HelpHome;
